// SIZE-EXCEPTION (2026-10-20): stays at ~995 lines - the remaining members cannot move to a helper file without a load-time import cycle (split files import this class only as `import type`).
import { kCsvCore } from "../kReusableCoreTS/kCsvCore";
import ks from "../ReUsableTS/kBrowserShared";
import { kObject, kObjectArray, kTime } from "../ReUsableTS/kBrowserObjects";
import { kObjectArrayCore } from "../kReusableCoreTS/kCoreObjects";
import {kSelect, kDateInput} from "../ReUsableTS/kHTML";
import { kHTMLMultiSelect, kMultiSelectOption } from "../ReUsableTS/kHTMLMultiSelect";
import { kContextMenu } from "../ReUsableTS/kContextMenu";
import { kEditableTableFilter, kColumnFilter, kFilterOperator } from "../ReUsableTS/kEditableTableFilter";
import { kSearchItems } from "../ReUsableTS/kSearch";
import { kExcelExport } from "../ReUsableTS/kExcelExport";
import { kModalDialog } from "../ReUsableTS/kModalDialog";
import LNG from "../ReUsableTS/kLNG";
import { kFluentIcons } from "./kFluentIcons";
import { kStyleClass, kStyles } from "./kStyles";
import { kBucketDragDrop } from "./kBucketDragDrop";
import { AsKTimeOrNull, kShouldGrowForTarget, kEditableTableActive, kHTMLEditableColType, kEdTblSelectionMode, kCellEditCommand, kEditTableFillIcons } from "./kEditableTableTypes";
import type { kRowFormHost, kRowFormFieldControl, kFilterUIController } from "./kEditableTableTypes";
export { kShouldGrowForTarget, kEditableTableActive, kBuildSelectEditOptions, kHTMLEditableColType, kStatusCellRenderer, kEdTblSelectionMode, GridValue, kCellEditCommand, kEditTableFillIcons } from "./kEditableTableTypes";
export type { kRowFormHost, kRowFormFieldControl, kFilterUIController, kRichHtmlCellEditorHost, kCellStatus, IkColumnSpec } from "./kEditableTableTypes";
import { kEditableTableRow, kEditableTableRows } from "./kEditableTableRow";
export { kEditableTableRow, kEditableTableRows } from "./kEditableTableRow";
import { kEditableColumn, kEditableColumns } from "./kEditableColumn";
export { kEditableColumn, kEditableColumns, kNewChoiceCollector } from "./kEditableColumn";
import { kEditableTableHeading } from "./kEditableTableHeading";
export { kEditableTableHeading } from "./kEditableTableHeading";
import { kEditableTableCell } from "./kEditableTableCell";
export { kEditableTableCell, kEditableTableCells } from "./kEditableTableCell";
import { kEditableCellSelection } from "./kEditableCellSelection";
import { kEditableTableLifecycle } from "./kEditableTableLifecycle";
import { kEditableTableCssText } from "./kEditableTableCss";
import { kEditableTableKeys } from "./kEditableTableKeys";
import { kEditableTableRender } from "./kEditableTableRender";
import { kEditableTablePaging } from "./kEditableTablePaging";
import { kEditableTableSearch } from "./kEditableTableSearch";
import { kEditableTableSortDrag } from "./kEditableTableSortDrag";
import { kEditableTableRowOps } from "./kEditableTableRowOps";
import { kEditableTableMenus } from "./kEditableTableMenus";
export { kEditableCellSelection } from "./kEditableCellSelection";

export class kEditableTable{

    private static readonly SELECTION_POLL_MS: number = 200;
    public static SetActiveTable = (Table: kEditableTable): void => {
        // DOCUMENTED EXCEPTION: pure one-line delegate; kEditableTableActive.SetActiveTable carries the guard, try/catch and handler (same label).
        kEditableTableActive.SetActiveTable(Table);
    }
    // Releases the grid's claim on the document-level key handler, so the very
    // next Tab leaves the grid instead of being consumed as cell navigation.
    // Without this the grid is a KEYBOARD TRAP: once it is the active table it
    // swallows every Tab, and a keyboard-only user can never get out. Bound to
    // Escape (when no inline edit is in progress) — the standard grid escape
    // hatch. Public so a host can release the grid programmatically too.
    public static ClearActiveTable = (Table: kEditableTable): void => {
        // DOCUMENTED EXCEPTION: pure one-line delegate; kEditableTableActive.ClearActiveTable carries the guard, try/catch and handler (same label).
        kEditableTableActive.ClearActiveTable(Table);
    }

    private _IntervalId: number = 0;

    constructor() {
        this.Selection.ParentTable = this;
        this.AddStyles();
        this._IntervalId = window.setInterval(this.CheckSelectionChange, kEditableTable.SELECTION_POLL_MS);
    }
    public Destroy = (): void => kEditableTableLifecycle.Destroy(this);
    public CellSelectionChangedCB:any = null;
    public SelectionChangedCB:any = null;
    public CellDirtiedCB:any = null;
    public RowDeletedCB:any = null;
    //public CellRightClickCB:any = null;
    public RowRightClickCB:any = null;
    public RowClickCB:any = null;
    public OnSorted: ((ColumnIndex: number, Ascending: boolean) => void) | null = null;

    // "Edit in form" — a row's right-click context menu offers an "Edit in form"
    // entry that pops a dialog editing the whole row as a form. The entry appears
    // automatically once a grid carries more than FormEditColumnThreshold data
    // columns (the wide-grid case where horizontal scrolling makes inline editing
    // awkward); EnableFormEdit forces it on for any width. The dialog itself lives
    // in kEditableRowForm and is wired in through RowFormFactory so the base grid
    // keeps no dependency on it (mirrors the RichHtml editor seam). Null factory
    // => the entry is suppressed.
    public EnableFormEdit: boolean = false;
    public FormEditColumnThreshold: number = 5;
    public static RowFormFactory: ((Row: kEditableTableRow) => kRowFormHost) | null = null;
    // Filter UI seam — mirrors RowFormFactory. The filter surfaces (header
    // funnel + popup, chip bar, advanced dialog) live in kEditableTableFilterUI;
    // importing that module registers this factory, so the base grid never
    // depends on the UI. Null factory => no per-column filter affordances are
    // built (the global quick-filter input still works on its own).
    public static FilterUIFactory: ((Grid: kEditableTable) => kFilterUIController) | null = null;
    public FilterUI: kFilterUIController | null = null;
    // Per-column filter affordances (header funnels + chip bar + advanced dialog +
    // context-menu filters) are ON BY DEFAULT — the UI factory auto-registers (see
    // the import at the bottom of this file), so a project needs no code to get
    // filtering. Set false to opt a grid out. The global quick-filter search box
    // (ShowFilterInput, which needs ShowToolbar) stays independent and opt-in.
    public ShowColumnFilters: boolean = true;
    // The faint magnitude bars behind Number/Currency cells. False = no bar in any
    // column (kEditableColumn.ShowDataBar turns one column off). Default on.
    public ShowDataBars: boolean = true;
    // The cells' context menu on a SINGLE-cell right-click too (not only on a range). Off by default:
    // a non-filter grid kept its range-only menu. A consumer that adds its own cell commands (via
    // OnCellsMenuSetup) turns it on. Added 2026-09-27 (DocumentBuilder UX-C, additive).
    public CellMenuOnSingleCell: boolean = false;
    // Called at the end of every SetupRangeContextMenu with the fresh menu, so a consumer's own items
    // (AddHeader / AddMenuItem) survive a rebuild of the menu (a read-only toggle rebuilds it). Null =
    // the built-in items only. Added 2026-09-27 (DocumentBuilder UX-C, additive).
    public OnCellsMenuSetup: ((Menu: kContextMenu) => void) | null = null;
    // true: a key whose target is a form field OUTSIDE this grid (an input, textarea, select or
    // contenteditable element of the surrounding form) is left to that field - the grid's
    // document-level key handler does not take Tab / arrows / Enter / type-to-edit from it. For a
    // grid that sits in a form with other fields. Off by default (the grid has always handled every
    // key while it is the active table); a grid whose own editors live outside its <table> must
    // leave it off. Added 2026-10-01 (DocumentBuilder PX-117, additive).
    public LeaveForeignFieldKeys: boolean = false;
    // true: Tab / Shift+Tab LEAVE the grid (the browser moves focus to the next control) and the grid
    // lets go of the document-level keys, instead of moving to the next / previous cell. For a grid
    // that is a list to pick from, not a sheet to type in (kPickGrid turns it on): the ARIA grid
    // pattern, where the grid is ONE tab stop and arrows move inside it. Off by default, so a
    // data-entry grid keeps its spreadsheet Tab (Escape still releases it). A cell in inline text
    // edit keeps Tab-to-next-cell either way. Added 2026-10-14 (Storage Clean-up, additive).
    public TabLeavesGrid: boolean = false;

    // Undo/Redo
    public MaxUndoSteps: number = 50;
    private _UndoStack: kCellEditCommand[] = [];
    private _RedoStack: kCellEditCommand[] = [];
    public RecordEdit = (Cell: kEditableTableCell, OldValue: any, NewValue: any): void => kEditableTableLifecycle.RecordEdit(this, Cell, OldValue, NewValue);
    public Undo = (): void => kEditableTableLifecycle.Undo(this);
    public Redo = (): void => kEditableTableLifecycle.Redo(this);

    public Columns: kEditableColumns = new kEditableColumns();

    public Table: HTMLTableElement = document.createElement("table");
    public RowValues:kObjectArrayCore|null = null;
    public Rows: kEditableTableRows = new kEditableTableRows();

    public CurrenEditableCell:kEditableTableCell|null = null;
    // Excel's two-mode model for the current single cell. FALSE = "ready" (just
    // selected via a single click or arrow navigation): arrows/Home/End/Shift+arrow
    // navigate or extend the CELL selection and Delete clears the cell. TRUE =
    // "active text edit" (entered via F2, double-click, a second click, a drag, or
    // typing into the cell): those keys instead move / extend the TEXT caret and
    // Delete/Backspace remove a character, leaving the keystrokes to the browser's
    // contentEditable. Reset to FALSE by SetSingleCell; set TRUE at the edit-entry
    // gestures. Only meaningful while CurrenEditableCell is contentEditable.
    public _InlineEditActive:boolean = false;
    public RangeSeleced:boolean = false;
    public Headers:Array<kEditableTableHeading> = new Array<kEditableTableHeading>();
    
    public RowContextMenu:kContextMenu|null = null;
    public ColContextMenu:kContextMenu|null = null;

    public CellsContextMenu:kContextMenu|null = null;
    // Context menu for the top-left corner cell (the "select all" cell where
    // the row-number column header meets the column headers). Surfaces the
    // whole-grid actions - Select all + Export to Excel - so the export is
    // discoverable without hunting through the per-row menu.
    public CornerContextMenu:kContextMenu|null = null;

    public RightClickColumn:kEditableTableHeading|null = null;
    public RightClickRow:kEditableTableRow|null = null;
    public RightClickCell:kEditableTableCell|null = null;

    public StartCell: kEditableTableCell | null = null;
    public Selection: kEditableCellSelection = new kEditableCellSelection();

    /**
     * WHOLE-TABLE READ-ONLY. One switch that closes every write path — default `false`, so every
     * existing consumer is byte-identical until it opts in.
     *
     * THE PROTECTION MODEL, WHOLE:
     *
     *     Column.Locked   this COLUMN cannot be written — not typed into, not blanked with Delete,
     *                     not pasted over, not filled into.
     *     Row.ReadOnly    this ROW cannot be written or removed.
     *     Table.ReadOnly  NOTHING in this grid can be written or removed, no blank add-row is
     *                     grown, and the destructive context-menu items are not offered.
     *
     * Each is complete on its own at its own scope, so there is no combination to remember: pick the
     * scope you mean. `ReadOnly` is applied to rows as they are BORN (`AddContentRows`), so a rebuild
     * cannot lose it, and `SetReadOnly()` retro-fits rows that already exist if it is flipped later.
     *
     * `IsEffectivelyReadOnly()` additionally reads "every column Locked" AS a read-only table, so a
     * grid built that way gets the whole-table behaviour without asking for it twice.
     */
    public ReadOnly: boolean = false;
    /**
     * Turn whole-table read-only on or off, applying it to rows that already exist.
     *
     * Rebuilds the context menus so the destructive items appear or disappear with the mode; safe to
     * call before the table is built (there are simply no rows or menus to update yet).
     */
    public SetReadOnly = (On: boolean): void => kEditableTableLifecycle.SetReadOnly(this, On);
    /**
     * Mark the TABLE element as whole-table read-only, so the stylesheet can tell that case apart
     * from "some rows are locked and others are not". See the .kEditableTableReadOnly rule.
     *
     * ⚠ IT ASKS `IsEffectivelyReadOnly()`, NOT `ReadOnly`. A grid whose every column is `Locked` is a
     * read-only grid that never had `ReadOnly` set — the exact shape a picker or a results table
     * ships — and it must read as lively as one that did. Same rule, and the same reason, as
     * BlankRowsAllowed.
     *
     * ⚠ CALLED FROM BOTH ToTable AND SetReadOnly, because the mode can be decided either before the
     * grid is built or after. A class stamped only at build time would be stale the first time a
     * consumer flipped the mode, which is exactly how the cell context menu came to be wrong.
     */
    private _PaintReadOnlyMode = (): void => kEditableTableLifecycle._PaintReadOnlyMode(this);
    /**
     * Is this grid one nobody may WRITE to — either because `ReadOnly` was set, or because every one
     * of its columns is `Locked`?
     *
     * The second reading is what lets a grid declare itself read-only by locking every column, which
     * is how most read-only grids in this estate are built. It carries the whole-table behaviour to
     * them — no Remove in the context menus, no blank add-row, no Delete-key blanking — without their
     * asking for it twice.
     *
     * ⚠ A grid that wants "no cell editing but rows may still be removed" leaves ONE column unlocked;
     * that is the shape this reading trades away, and nothing in the estate is built that way.
     *
     * ⚠ AN EMPTY COLUMN SET IS **NOT** READ-ONLY. A grid mid-construction has no columns yet, and
     * answering `true` there would refuse writes on a table that has not been configured — failing in
     * the direction of "nothing works" for a reason the caller cannot see.
     */
    public IsEffectivelyReadOnly = (): boolean => kEditableTableLifecycle.IsEffectivelyReadOnly(this);
    /**
     * May the grid grow a trailing blank add-row right now?
     *
     * ONE answer for the three places that used to read `AddBlankRows` directly, so read-only cannot
     * be honoured by two of them and forgotten by the third.
     *
     * ⚠ IT ASKS `IsEffectivelyReadOnly()`, NOT `ReadOnly`, AND THAT IS THE POINT. A grid whose every
     * column is `Locked` cannot be typed into, so a trailing blank row there is a row the operator
     * can never fill — an add-row that does not add. Reading the effective answer means the fully
     * locked grid gets that right without also being told `AddBlankRows = false`, which is the second
     * switch this method exists to abolish. Measured 2026-08-25 against every all-locked grid in the
     * estate — MoseAreasGrid, Dina SiteHub TableView, FleetAdminJobsView, FleetAdminIncidentsView —
     * all four already set `AddBlankRows = false` by hand, so nothing's behaviour changes; what
     * changes is that the FIFTH one cannot get it wrong. FleetAdminEntityView keeps its blank row:
     * it locks only its ID and details columns, so it is not effectively read-only.
     */
    public BlankRowsAllowed = (): boolean => kEditableTableLifecycle.BlankRowsAllowed(this);

    public AddBlankRows: boolean = true;
    // When true, the trailing blank "add" row renders Number / Currency cells EMPTY
    // instead of "0", so an untouched add-row reads as blank rather than a phantom
    // "0" value (e.g. a new Location showing lat/long 0/0). Default false preserves the
    // original "0" placeholder for every existing caller.
    public BlankNumberCellsEmpty: boolean = false;
    // When set (non-empty), the trailing blank "add" row's FIRST cell is marked with the
    // class kEditableTableAddHint + data-addhint=<this text>, so a consumer's CSS can render
    // a faint "+ new…" placeholder (via td.kEditableTableAddHint:empty::before { content:
    // attr(data-addhint) }). Makes the always-present add-row read as an add affordance, not
    // stray data. Default "" ⇒ no marker (unchanged for every existing caller).
    public AddRowHint: string = "";
    public PasteInProgress: boolean = false;
    public AlternateRowShading: boolean = false;
    public AlternateRowColor: string = "rgba(129,71,255,0.03)";

    // Added 2026-05-13: opt-in container with toolbar / footer host slots
    // and a theme class hook. ToTable() is unchanged for existing callers;
    // ToContainer() wraps it with the slots when these flags are set.
    public IdPrefix: string = "kETbl";
    /** The grid's accessible name (aria-label on the role=grid table), e.g. "Largest files". Empty (the
     *  default) = no attribute, as before. Set before ToTable / ToContainer. */
    public AriaLabel: string = "";
    public ThemeClass: string = "";
    public ShowToolbar: boolean = false;
    public ShowFooter: boolean = false;
    public ShowFilterInput: boolean = false;
    public ShowRowCountInFooter: boolean = false;
    public EnableRowReorder: boolean = false;
    public RowReorderedCB: any = null;
    public _DraggedRowIndex: number = -1;
    // "" means "use the translated default" (LNG.SEARCH), resolved in AppendFilterInput at
    // RENDER time, not here: a field initialiser runs when the grid is constructed, which can be
    // before the product has set its language, and would freeze the English string. A caller
    // that sets its own text keeps it. (Was a hard-coded English "Filter rows…" until 2026-09-24.)
    public FilterPlaceholder: string = "";
    // Quick-filter debounce (2026-09-24). Typing in the toolbar search box re-filters the whole
    // grid; on a 1,000-row grid that measured 165-572 ms PER KEYSTROKE, so typing stuttered. The
    // box now waits until typing pauses for this many ms, then filters once. 0 (or less) = filter
    // on every keystroke, the old behaviour. Enter applies a pending filter at once, and clearing
    // the box (empty term) is always immediate - restoring every row is one deliberate action,
    // not a keystroke stream. Programmatic surfaces (ApplyFilters, SetColumnFilter, ClearFilters,
    // SetFilterState) are never debounced; ClearFilters / SetFilterState / Destroy cancel a
    // pending box filter so a stale term cannot land after them.
    public FilterDebounceMs: number = 250;
    private _FilterDebounceTimer: number = 0;
    public ContainerDiv: HTMLDivElement | null = null;
    public ToolbarDiv: HTMLDivElement | null = null;
    public FooterDiv: HTMLDivElement | null = null;
    public FilterInput: HTMLInputElement | null = null;
    public RowCountSpan: HTMLSpanElement | null = null;

    // Pagination — OFF by default. When EnablePagination is true, ToContainer()
    // renders a pager bar (Previous / "Page X of Y" / Next) below the grid and
    // only PageSize eligible rows (non-deleted rows that pass the active filter)
    // are shown at a time. Filtering and paging compose: the pager pages over the
    // filter-passing set, and changing the filter re-pages from page 1's window.
    // Requires ToContainer() (ToTable() alone renders no pager). PageChangedCB, if
    // set, fires with the clamped current page and the total page count.
    public EnablePagination: boolean = false;
    public PageSize: number = 20;
    private _CurrentPage: number = 0;
    public PaginationDiv: HTMLDivElement | null = null;
    public PrevPageButton: HTMLButtonElement | null = null;
    public NextPageButton: HTMLButtonElement | null = null;
    public PageInfoSpan: HTMLSpanElement | null = null;
    public PageChangedCB: ((Page: number, PageCount: number) => void) | null = null;
    // Fires at the end of every ApplyFilters (typing in the quick filter, a header funnel, the
    // advanced dialog, a programmatic filter) with the number of filter-passing rows. PageChangedCB
    // does NOT fire when a filter re-pages the grid, so "the rows on screen changed" needs both.
    public FilterAppliedCB: ((VisibleCount: number) => void) | null = null;
    // The whole filter state for this grid (per-column conditions + global
    // term). The global quick-filter input, the header funnel popups, the
    // context-menu entries and the advanced dialog all read and write this one
    // model; ApplyFilters() is the single place that turns it into row
    // visibility. See kEditableTableFilter.
    public FilterModel: kEditableTableFilter = new kEditableTableFilter();
    // Shared substring highlighter (kSearch) for the global quick-filter — wraps
    // matched text in <span class="highlight"> on read-mode text cells. Built
    // lazily so non-filtering grids never construct it.
    private _Highlighter: kSearchItems | null = null;
    // Shown in place of the rows when an active filter hides every row.
    public NoMatchRow: HTMLTableRowElement | null = null;
    public NoMatchText: string = "No rows match the current filter";
    public UpdateRowNumbers = (): void => kEditableTableLifecycle.UpdateRowNumbers(this);

    // True when the "Edit in form" menu entry should be offered: a factory is
    // registered AND the grid is either explicitly opted in or wider than the
    // threshold.
    public IsFormEditEnabled = (): boolean => {
        if (ks.KErrorMode === true) { return false; }
        try {
            if (kEditableTable.RowFormFactory === null) { return false; }
            if (this.EnableFormEdit === true) { return true; }
            return this.Columns.Items.length > this.FormEditColumnThreshold;
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTable.IsFormEditEnabled");
            return false;
        }
    }

    // Opens the row-as-form dialog for the given row via the registered
    // factory. Public so consumers can wire their own affordance (e.g. an
    // explicit toolbar button) to the same dialog.
    public OpenRowForm = (Row: kEditableTableRow): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            if (kEditableTable.RowFormFactory === null) { return; }
            const Host: kRowFormHost = kEditableTable.RowFormFactory(Row);
            Host.Open();
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTable.OpenRowForm");
        }
    }
    public ApplyAlternateRowShading = (): void => kEditableTableLifecycle.ApplyAlternateRowShading(this);
    public OpenRichHtmlEditor = (Cell: kEditableTableCell): void => kEditableTableLifecycle.OpenRichHtmlEditor(this, Cell);
    public CreateRichHtmlInlineField = (Cell: kEditableTableCell): kRowFormFieldControl | null => kEditableTableLifecycle.CreateRichHtmlInlineField(this, Cell);
    public AddColumn = (Title:string, Type:kHTMLEditableColType, Width:number, PropName:string, DefaultValue?:any, IconImg?:HTMLImageElement): kEditableColumn => kEditableTableLifecycle.AddColumn(this, Title, Type, Width, PropName, DefaultValue, IconImg);
    // Named keys that, WHILE a Text/Number/Currency cell is in ACTIVE text-edit
    // mode, move or extend the TEXT caret/selection or delete within the text.
    // They belong to the browser's contentEditable, NOT the grid's cell navigation.
    private static _TextEditCaretKeys: string[] = ["ArrowLeft", "ArrowRight", "Home", "End", "Backspace", "Delete"];
    // True when a key pressed inside an inline text editor must be left to the
    // browser so it acts on the cell text, not the grid.
    //   - Printable characters (key.length === 1, including Ctrl/Meta chords such
    //     as Ctrl+A/C/V/X/Z for in-field select-all / clipboard / undo, and typing
    //     that replaces a selected sub-range) are ALWAYS deferred — even in "ready"
    //     mode, typing overwrites the cell (Excel type-over).
    //   - The caret / selection / delete keys above are deferred ONLY once the cell
    //     is in ACTIVE edit mode (_InlineEditActive), so Shift+Arrow extends the
    //     text selection, Home/End jump within the text, Ctrl+Arrow word-jumps and
    //     Delete/Backspace remove a character. In merely-selected "ready" mode they
    //     stay with the grid (navigate cells / extend the CELL selection / clear).
    // Structural keys — Enter, Tab, Escape, ArrowUp, ArrowDown, F2, Page* — are
    // never deferred here: they commit/cancel the edit and move between cells.
    private _IsTextEditCaretKey = (KeyEvent: KeyboardEvent): boolean => {
        if (ks.KErrorMode === true) { return false; }
        try {
            if (KeyEvent.key.length === 1) { return true; }
            if (this._InlineEditActive === true
                && kEditableTable._TextEditCaretKeys.indexOf(KeyEvent.key) !== -1) {
                return true;
            }
            return false;
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTable._IsTextEditCaretKey");
            return false;
        }
    }
    private _KeyDownShouldIgnore = (KeyEvent:KeyboardEvent): boolean => kEditableTableKeys._KeyDownShouldIgnore(this, KeyEvent);
    private _KeyTargetBelongsElsewhere = (Target: EventTarget | null): boolean => kEditableTableKeys._KeyTargetBelongsElsewhere(this, Target);
    private _IsForeignField = (Target: HTMLElement): boolean => kEditableTableKeys._IsForeignField(this, Target);
    private _TryTypeToEdit = (KeyEvent:KeyboardEvent): boolean => kEditableTableKeys._TryTypeToEdit(this, KeyEvent);
    public KeyDown = (KeyEvent:KeyboardEvent): void => kEditableTableKeys.KeyDown(this, KeyEvent);
    public ShiftKeyEvents = (key:string): boolean => kEditableTableKeys.ShiftKeyEvents(this, key);
    /** Shift+Tab — step the selection one cell left, wrapping to the row above. */
    private _ShiftTab = (): boolean => kEditableTableKeys._ShiftTab(this);
    /** Shift+Space — promote a single-cell selection to its whole row. */
    private _ShiftSpaceSelectsRow = (): boolean => kEditableTableKeys._ShiftSpaceSelectsRow(this);
    public CTRLKeyevents = (key:string, KeyEvent:KeyboardEvent): boolean => kEditableTableKeys.CTRLKeyevents(this, key, KeyEvent);
    /** CTRL + SHIFT + key. Returns true when the key was consumed. */
    private _CtrlShiftKey = (key:string): boolean => kEditableTableKeys._CtrlShiftKey(this, key);
    /** CTRL + SHIFT + End — stretch the selection to the last column / row. */
    private _CtrlShiftEnd = (): boolean => kEditableTableKeys._CtrlShiftEnd(this);
    /** CTRL + key (no Shift). Returns true when the key was consumed. */
    private _CtrlOnlyKey = (key:string): boolean => kEditableTableKeys._CtrlOnlyKey(this, key);
    /** CTRL+Space — promote a single-cell selection to its whole column. */
    private _CtrlSpaceSelectsColumn = (): boolean => kEditableTableKeys._CtrlSpaceSelectsColumn(this);
    private _HandleArrowKey = (Key: string): boolean => kEditableTableKeys._HandleArrowKey(this, Key);
    private _HandleEditGestureKey = (Key: string): boolean => kEditableTableKeys._HandleEditGestureKey(this, Key);
    private _HandleEnterEditGesture = (): boolean => kEditableTableKeys._HandleEnterEditGesture(this);
    private _HandleTabKey = (): boolean => kEditableTableKeys._HandleTabKey(this);
    private _HandleCellCommandKey = (Key: string): boolean => kEditableTableKeys._HandleCellCommandKey(this, Key);
    private _HandleDeleteKey = (): boolean => kEditableTableKeys._HandleDeleteKey(this);

    private _HandleEscapeKey = (): boolean => {
        if (ks.KErrorMode === true) { return false; }
        try {
            // Esc-cancels-edit: only when a Text/Number/Currency cell is
            // actively in inline text-edit mode; otherwise leave Escape for
            // whatever owns it (popup editors close via their own listeners).
            if (this.CurrenEditableCell && this.CurrenEditableCell.Cell.contentEditable === "true") {
                this.CurrenEditableCell.CancelTextEdit();
                return true;
            }
            // Not editing: Escape is the grid's ESCAPE HATCH. The grid
            // consumes every Tab while it is the active table, which made
            // it a keyboard trap once entered. Releasing the claim (WITHOUT
            // reporting the key as handled, so a host dialog / popup that also
            // owns Escape still receives it) lets the very next Tab move
            // focus out of the grid normally.
            kEditableTable.ClearActiveTable(this);
            return false;
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTable._HandleEscapeKey");
            return false;
        }
    }
    private _HandleHomeEndKey = (Key: string): boolean => kEditableTableKeys._HandleHomeEndKey(this, Key);
    private _HandleNoModifierKey = (Key: string): boolean => kEditableTableKeys._HandleNoModifierKey(this, Key);
    private _EnterFromKeyboard = (Key: string): boolean => kEditableTableKeys._EnterFromKeyboard(this, Key);
    private _ActivateCellControl = (): boolean => kEditableTableKeys._ActivateCellControl(this);
    private _TryOpenFilterKey = (KeyEvent: KeyboardEvent): boolean => kEditableTableKeys._TryOpenFilterKey(this, KeyEvent);
    private _OpenActiveColumnFilter = (): boolean => kEditableTableKeys._OpenActiveColumnFilter(this);

    // TabLeavesGrid: let the browser move focus on and let go of the document-level keys. Not while a
    // cell is in inline text edit - there Tab still commits and moves to the next cell.
    private _TabLeavesGrid = (): boolean => {
        if (ks.KErrorMode === true) { return false; }
        try {
            if (this.TabLeavesGrid === false) { return false; }
            if (this._InlineEditActive === true) { return false; }
            kEditableTable.ClearActiveTable(this);
            return true;
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTable._TabLeavesGrid");
            return false;
        }
    }

    private _prevSelectionUnique:number =0;
    public CheckSelectionChange = (): void => kEditableTableKeys.CheckSelectionChange(this);
    public SetParentSize = (Height:number): void => kEditableTableKeys.SetParentSize(this, Height);
    public AddStyles = (): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            kStyles.AddStyles("kEditableTableStyles", kEditableTable._Css);
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTable.AddStyles");
        }
    }

    /** Stylesheet text only — injected by AddStyles. */
    private static _Css: string = kEditableTableCssText;
    public RemoveContentRows = (): void => kEditableTableRender.RemoveContentRows(this);
    public GetGridContentsTokObjectArray = (NewItemObject:any, NewArrayObject:any): any => kEditableTableRender.GetGridContentsTokObjectArray(this, NewItemObject, NewArrayObject);
    private _ReconcileActiveEditForHarvest = (): void => kEditableTableRender._ReconcileActiveEditForHarvest(this);
    public CommitActiveEdit = (): void => kEditableTableRender.CommitActiveEdit(this);
    private _ConvertCellValue = (CurrCell: kEditableTableCell, ColDef: kEditableColumn): any => kEditableTableRender._ConvertCellValue(this, CurrCell, ColDef);
    private _ConvertByColType = (CurrCell: kEditableTableCell, ColDef: kEditableColumn): any => kEditableTableRender._ConvertByColType(this, CurrCell, ColDef);
    public AddContentRows = (): void => kEditableTableRender.AddContentRows(this);
    /**
     * Build ONE content row — its identity, its whole-table read-only stamp, and its cells.
     *
     * Split out of `AddContentRows` on 2026-08-25, when the read-only stamp took that method past the
     * 40-line rule. It is a better seam than the line count made it: this is the one place a row is
     * BORN, which is exactly where `ReadOnly` has to reach so a rebuild cannot lose it, while
     * `AddContentRows` is left saying only how the rows are strung together.
     */
    private _CreateContentRow = (CurrItem: kObject, CurrRowIndex: number): kEditableTableRow => kEditableTableRender._CreateContentRow(this, CurrItem, CurrRowIndex);

    private _CreateContentCell = (CurrItem: kObject, CurrCol: kEditableColumn, CurrRowIndex: number, CurrColIndex: number): kEditableTableCell => {
        if (ks.KErrorMode === true) { return new kEditableTableCell(); }
        try {
            const NewCell: kEditableTableCell = new kEditableTableCell();
            NewCell.RowIndex = CurrRowIndex;
            NewCell.ColumnIndex = CurrColIndex;
            NewCell.Title = "Cell r" + CurrRowIndex.toString() + " c" + CurrColIndex.toString();
            NewCell.ColDef = CurrCol;
            // Locked class, per-column CssClass and inline-important TintColor (shared with the header).
            CurrCol.ApplyStyleTo(NewCell.Cell, "kEditableTableCellLocked");
            kEditableTable._SeedNumericCell(NewCell);
            NewCell.ParentTable = this;
            kEditableTable._SeedCellFromItem(NewCell, CurrItem, CurrCol);
            if (CurrCol.Width === 0) { NewCell.Cell.style.display = "none"; }
            if (CurrCol._FrozenLeft >= 0) {
                NewCell.Cell.style.position = "sticky";
                NewCell.Cell.style.left = CurrCol._FrozenLeft + "px";
                NewCell.Cell.style.zIndex = "1";
                NewCell.Cell.style.background = "inherit";
            }
            return NewCell;
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTable._CreateContentCell");
            return new kEditableTableCell();
        }
    }

    /** Right-aligns a Number/Currency cell and seeds it with a zero. */
    private static _SeedNumericCell = (NewCell: kEditableTableCell): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            if (NewCell.ColDef.Type !== kHTMLEditableColType.Number
                && NewCell.ColDef.Type !== kHTMLEditableColType.Currency) { return; }
            NewCell.Cell.style.textAlign = "right";
            NewCell.OrgValue = 0;
            if (NewCell.ColDef.Type === kHTMLEditableColType.Number) {
                NewCell.Cell.textContent = "0";
            } else {
                NewCell.Cell.textContent = ks.LocalCurrencyNumber(0);
            }
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTable._SeedNumericCell");
        }
    };

    /** Reads the column's property off the domain item into the cell's OrgValue. */
    private static _SeedCellFromItem = (NewCell: kEditableTableCell, CurrItem: kObject, CurrCol: kEditableColumn): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            switch (CurrCol.Type) {
                case kHTMLEditableColType.Date: {
                    const CurrDate: kTime | null = (CurrItem as any)[CurrCol.KObjectPropertyName];
                    if (CurrDate) { NewCell.SetCellOrgValue(CurrDate); }
                    break;
                }
                case kHTMLEditableColType.SelectMulti:
                    NewCell.SetCellOrgValue((CurrItem as any)[CurrCol.KObjectPropertyName].join("; "));
                    break;
                default:
                    NewCell.SetCellOrgValue((CurrItem as any)[CurrCol.KObjectPropertyName]);
                    break;
            }
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTable._SeedCellFromItem");
        }
    };
    /** role=grid, the grid's accessible name when the host gave one (AriaLabel), and its ONE tab stop. */
    private _PaintGridRole = (): void => kEditableTableRender._PaintGridRole(this);
    public ToTable = (): HTMLTableElement => kEditableTableRender.ToTable(this);
    public ApplyDataBars = (): void => kEditableTableRender.ApplyDataBars(this);
    public ApplyColumnDataBars = (ColumnIndex: number): void => kEditableTableRender.ApplyColumnDataBars(this, ColumnIndex);

    // Added 2026-05-13: wraps ToTable() with optional toolbar and footer
    // host slots and applies ThemeClass to the wrapping container. Callers
    // fill ToolbarDiv / FooterDiv themselves after the container is in the
    // DOM. Use this instead of ToTable() when you want filter inputs,
    // import buttons, or a row-count summary around the grid.
    public ToContainer = (): HTMLDivElement => {
        if (ks.KErrorMode === true) { return document.createElement("div"); }
        try {
            const Container: HTMLDivElement = document.createElement("div");
            Container.id = this.IdPrefix + "-container";
            Container.className = "kEditableTableContainer";
            if (this.ThemeClass !== "") {
                Container.classList.add(this.ThemeClass);
            }
            if (this.ShowToolbar === true) {
                const Toolbar: HTMLDivElement = document.createElement("div");
                Toolbar.id = this.IdPrefix + "-toolbar";
                Toolbar.className = "kEditableTableToolbar";
                Container.appendChild(Toolbar);
                this.ToolbarDiv = Toolbar;
                if (this.ShowFilterInput === true) { this.AppendFilterInput(Toolbar); }
            }
            Container.appendChild(this.ToTable());
            if (this.EnableRowReorder === true) { this.ActivateDragReorder(); }
            if (this.ShowFooter === true) {
                const Footer: HTMLDivElement = document.createElement("div");
                Footer.id = this.IdPrefix + "-footer";
                Footer.className = "kEditableTableFooter";
                Container.appendChild(Footer);
                this.FooterDiv = Footer;
                if (this.ShowRowCountInFooter === true) { this.AppendRowCountSpan(Footer); }
            }
            this.ContainerDiv = Container;
            if (this.ShowColumnFilters === true && kEditableTable.FilterUIFactory !== null) {
                this.FilterUI = kEditableTable.FilterUIFactory(this);
            }
            if (this.EnablePagination === true) { this._SetupPagination(Container); }
            return Container;
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTable.ToContainer");
            return document.createElement("div");
        }
    }
    private AppendFilterInput = (Toolbar: HTMLDivElement): void => kEditableTablePaging.AppendFilterInput(this, Toolbar);
    private OnFilterInput = (): void => kEditableTablePaging.OnFilterInput(this);
    private OnFilterKeyDown = (KeyEvent: KeyboardEvent): void => kEditableTablePaging.OnFilterKeyDown(this, KeyEvent);
    private _OnFilterDebounceElapsed = (): void => kEditableTablePaging._OnFilterDebounceElapsed(this);
    private _ApplyFilterInputNow = (): void => kEditableTablePaging._ApplyFilterInputNow(this);
    public CancelPendingFilter = (): void => kEditableTablePaging.CancelPendingFilter(this);
    public HasPendingFilter = (): boolean => kEditableTablePaging.HasPendingFilter(this);
    public ApplyFilters = (): number => kEditableTablePaging.ApplyFilters(this);
    private _SafePageSize = (): number => kEditableTablePaging._SafePageSize(this);
    private _ShowPageWindow = (Eligible: kEditableTableRow[]): void => kEditableTablePaging._ShowPageWindow(this, Eligible);
    public GetPageCount = (): number => kEditableTablePaging.GetPageCount(this);
    public GetCurrentPage = (): number => kEditableTablePaging.GetCurrentPage(this);
    public GoToPage = (PageIndex: number): void => kEditableTablePaging.GoToPage(this, PageIndex);
    public NextPage = (): void => kEditableTablePaging.NextPage(this);
    public PrevPage = (): void => kEditableTablePaging.PrevPage(this);
    private _SetupPagination = (Container: HTMLDivElement): void => kEditableTablePaging._SetupPagination(this, Container);
    private _BuildPaginationBar = (Container: HTMLDivElement): void => kEditableTablePaging._BuildPaginationBar(this, Container);
    private _OnPrevPageClick = (): void => kEditableTablePaging._OnPrevPageClick(this);
    private _OnNextPageClick = (): void => kEditableTablePaging._OnNextPageClick(this);
    private _UpdatePaginationControls = (PageCount: number): void => kEditableTablePaging._UpdatePaginationControls(this, PageCount);
    public _ReapplyIfFiltering = (): void => kEditableTablePaging._ReapplyIfFiltering(this);
    private _ApplyGlobalHighlight = (): void => kEditableTableSearch._ApplyGlobalHighlight(this);
    private _HighlightCell = (Cell: kEditableTableCell, Words: string[]): void => kEditableTableSearch._HighlightCell(this, Cell, Words);
    private _HighlightTextNodes = (Root: HTMLElement, Words: string[]): void => kEditableTableSearch._HighlightTextNodes(this, Root, Words);

    // Replace one text node with a fragment where the matched words are wrapped in
    // <span class="highlight">, via kSearch's multi-word highlighter. HTML is escaped
    // before wrapping; a node with no match is left untouched.
    private _HighlightOneTextNode = (TextNode: Text, Words: string[]): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            const Text: string = ks.Nz(TextNode.nodeValue);
            if (this._TextContainsAnyWord(Text, Words) === false) { return; }
            const Highlighter: kSearchItems | null = this._GetHighlighter();
            if (Highlighter === null) { return; }
            Highlighter.CurrSearchTerm = Words.join(" ");
            const Html: string = Highlighter.GetHighlightedTextMultiWord(kEditableTable._EscapeHtml(Text));
            const Holder: HTMLSpanElement = document.createElement("span");
            Holder.innerHTML = Html;
            const Frag: DocumentFragment = document.createDocumentFragment();
            while (Holder.firstChild !== null) { Frag.appendChild(Holder.firstChild); }
            if (TextNode.parentNode !== null) { TextNode.parentNode.replaceChild(Frag, TextNode); }
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTable._HighlightOneTextNode");
        }
    }
    private _UnwrapHighlights = (Root: HTMLElement): void => kEditableTableSearch._UnwrapHighlights(this, Root);
    private _CellHostsEditor = (Td: HTMLTableCellElement): boolean => kEditableTableSearch._CellHostsEditor(this, Td);
    private _TextContainsAnyWord = (Text: string, Words: string[]): boolean => kEditableTableSearch._TextContainsAnyWord(this, Text, Words);
    private _SplitWords = (Term: string): string[] => kEditableTableSearch._SplitWords(this, Term);
    private _GetHighlighter = (): kSearchItems | null => kEditableTableSearch._GetHighlighter(this);

    private static _EscapeHtml = (Text: string): string => {
        if (ks.KErrorMode === true) { return ""; }
        try {
            return Text.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTable._EscapeHtml");
            return "";
        }
    }
    private _UpdateNoMatchRow = (Visible: number): void => kEditableTableSearch._UpdateNoMatchRow(this, Visible);
    private _BuildNoMatchRow = (): void => kEditableTableSearch._BuildNoMatchRow(this);

    // Programmatic filter API (feature #18). Consumers and tests drive filtering
    // in code without touching any UI surface.
    public SetColumnFilter = (PropName: string, Operator: kFilterOperator, Value: string,
        Value2?: string, SelectedValues?: string[]): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            const Column: kEditableColumn | null = this._ColumnByProp(PropName);
            if (Column === null) { return; }
            const Filter: kColumnFilter = new kColumnFilter();
            Filter.PropName = PropName;
            Filter.ColType = Column.Type;
            Filter.Title = Column.Title;
            Filter.Operator = Operator;
            Filter.Value = ks.Nz(Value);
            Filter.Value2 = ks.Nz(Value2);
            if (SelectedValues !== undefined) { Filter.SelectedValues = SelectedValues; }
            this.FilterModel.SetColumnFilter(Filter);
            this.ApplyFilters();
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTable.SetColumnFilter");
        }
    }
    public ClearColumnFilter = (PropName: string): void => kEditableTableSearch.ClearColumnFilter(this, PropName);
    public ClearFilters = (): void => kEditableTableSearch.ClearFilters(this);
    public GetFilterState = (): string => kEditableTableSearch.GetFilterState(this);
    public SetFilterState = (Json: string): void => kEditableTableSearch.SetFilterState(this, Json);
    /** The two filter resets in one shell: cancel the pending edit, apply the model, mirror the term to the input, re-apply. */
    private _ResetFilters = (Model: () => void, Term: () => string, Label: string): void => kEditableTableSearch._ResetFilters(this, Model, Term, Label);
    private _ColumnByProp = (PropName: string): kEditableColumn | null => kEditableTableSearch._ColumnByProp(this, PropName);
    public RefreshFilterIndicators = (): void => kEditableTableSearch.RefreshFilterIndicators(this);
    private _ColumnByIndex = (Index: number): kEditableColumn | null => kEditableTableSearch._ColumnByIndex(this, Index);
    private AppendRowCountSpan = (Footer: HTMLDivElement): void => kEditableTableSearch.AppendRowCountSpan(this, Footer);
    public UpdateRowCount = (Count: number): void => kEditableTableSearch.UpdateRowCount(this, Count);
    public SetRowSaveState = (Index: number, State: string): void => kEditableTableSortDrag.SetRowSaveState(this, Index, State);
    public ActivateDragReorder = (): void => kEditableTableSortDrag.ActivateDragReorder(this);
    private _WireRowDrag = (Row: kEditableTableRow): void => kEditableTableSortDrag._WireRowDrag(this, Row);
    public OnRowDragStart = (Evt: DragEvent): void => kEditableTableSortDrag.OnRowDragStart(this, Evt);
    public OnRowDragOver = (Evt: DragEvent): void => kEditableTableSortDrag.OnRowDragOver(this, Evt);
    public OnRowDrop = (Evt: DragEvent): void => kEditableTableSortDrag.OnRowDrop(this, Evt);
    private _MoveRow = (FromIdx: number, ToIdx: number): void => kEditableTableSortDrag._MoveRow(this, FromIdx, ToIdx);
    public ImportCsvText = (Text: string): void => kEditableTableSortDrag.ImportCsvText(this, Text);
    private _CreateColumnHeaders = (): void => kEditableTableSortDrag._CreateColumnHeaders(this);
    private _BuildColumnHeader = (CurrCol: kEditableColumn, FrozenLeftOffset: number): number => kEditableTableSortDrag._BuildColumnHeader(this, CurrCol, FrozenLeftOffset);
    public MouseEnter = (Event:MouseEvent): void => {
        if (ks.KErrorMode === true) { return ; }
        try {
            if(this.Table !== null && (this.Table as HTMLElement).offsetParent === null){
                return;
            }
            // No Table.focus() here (removed 2026-10-14): it was a no-op while the <table> had no
            // tabindex, and now that the table IS focusable it would steal focus from the field the
            // user is typing in (the search box) whenever the mouse crosses the grid.
            kEditableTable.SetActiveTable(this);
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTable.MouseEnter");
            return ;
        }
    }

    /** The keyboard entered the grid (its one tab stop): it becomes the active table, as the mouse makes it. */
    public OnTableFocus = (): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            kEditableTable.SetActiveTable(this);
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTable.OnTableFocus");
        }
    };
    public SortByColumn = (ColumnIndex:number): void => kEditableTableSortDrag.SortByColumn(this, ColumnIndex);
    public SortAscending = (ColumnIndex?:number): void => kEditableTableSortDrag.SortAscending(this, ColumnIndex);
    public SortDescending = (ColumnIndex?:number): void => kEditableTableSortDrag.SortDescending(this, ColumnIndex);
    /** The two sort directions in one shell: mark the header (ascending also bolds), sort the table, report, re-apply filters. */
    private _SortTo = (ColumnIndex: number | undefined, Ascending: boolean, Label: string): void => kEditableTableSortDrag._SortTo(this, ColumnIndex, Ascending, Label);
    private _GetSortableValue = (Cell: kEditableTableCell): any => kEditableTableSortDrag._GetSortableValue(this, Cell);

    private _SortColumnIndex: number = 0;
    private _SortAscending: boolean = true;
    private _RowComparator = (A: kEditableTableRow, B: kEditableTableRow): number => kEditableTableSortDrag._RowComparator(this, A, B);
    public SortTable = (ColumnIndex: number, Ascending: boolean): void => kEditableTableSortDrag.SortTable(this, ColumnIndex, Ascending);
    private _SetOtherUnsorted = (ColumnIndex:number): void => kEditableTableSortDrag._SetOtherUnsorted(this, ColumnIndex);

    // Column resizing
    private _ResizingHeader: kEditableTableHeading | null = null;
    private _ResizeStartX: number = 0;
    private _ResizeStartWidth: number = 0;
    private _AddResizeHandle = (Header: kEditableTableHeading): void => kEditableTableSortDrag._AddResizeHandle(this, Header);
    private _OnResizeMouseDown = (Event: MouseEvent): void => kEditableTableSortDrag._OnResizeMouseDown(this, Event);
    private _OnResizeMouseMove = (Event: MouseEvent): void => kEditableTableSortDrag._OnResizeMouseMove(this, Event);
    private _OnResizeMouseUp = (): void => kEditableTableSortDrag._OnResizeMouseUp(this);

    // Virtual scrolling
    public VirtualScrollMaxHeight: number = 0;
    private _ScrollWrapper: HTMLDivElement | null = null;
    private _RowObserver: IntersectionObserver | null = null;
    public WrapInScrollContainer = (): HTMLDivElement => kEditableTableSortDrag.WrapInScrollContainer(this);
    public SetVirtualScrollHeight = (HeightPx: number): void => kEditableTableSortDrag.SetVirtualScrollHeight(this, HeightPx);
    private _SetupIntersectionObserver = (): void => kEditableTableSortDrag._SetupIntersectionObserver(this);
    private _OnRowVisibilityChange = (Entries: IntersectionObserverEntry[]): void => kEditableTableSortDrag._OnRowVisibilityChange(this, Entries);
    public AddEmptyRows = (NumberOfRows:number): void => kEditableTableRowOps.AddEmptyRows(this, NumberOfRows);

    /** One blank cell of an APPENDED row (AddEmptyRows). Honours BlankNumberCellsEmpty
     *  and lets a CustomRenderer paint its empty state — InsertRowAbove's
     *  _CreateInsertedRowCell deliberately does neither; see the note there. */
    private _CreateEmptyRowCell = (CurrCol: kEditableColumn, RowIndex: number, ColIndex: number): kEditableTableCell => {
        if (ks.KErrorMode === true) { return new kEditableTableCell(); }
        try {
            const NewCell: kEditableTableCell = new kEditableTableCell();
            NewCell.RowIndex = RowIndex;
            NewCell.ColumnIndex = ColIndex;
            NewCell.ColDef = CurrCol;
            this._PaintBlankCellByType(NewCell, CurrCol);
            NewCell.ParentTable = this;
            if (CurrCol.DefaultValue) {
                NewCell.SetCellOrgValue(CurrCol.DefaultValue);
            } else {
                NewCell.OrgValue = "";
                // Empty cells in a CustomRenderer column still need the renderer to
                // paint the empty-state affordance (e.g. an "Assign…" placeholder +
                // edit button). SetCellOrgValue is skipped here because the org value
                // is "", so call the renderer directly.
                if (CurrCol.CustomRenderer !== null) {
                    CurrCol.CustomRenderer("", NewCell);
                    kEditableTableCell.KeepOutOfTabOrder(NewCell.Cell);
                }
            }
            kEditableTable._ApplyColumnLayoutToCell(NewCell, CurrCol);
            return NewCell;
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTable._CreateEmptyRowCell");
            return new kEditableTableCell();
        }
    };
    /** Paints the blank read-mode look of an APPENDED row's cell for its column type. */
    private _PaintBlankCellByType = (NewCell: kEditableTableCell, CurrCol: kEditableColumn): void => kEditableTableRowOps._PaintBlankCellByType(this, NewCell, CurrCol);

    /** The hide-if-zero-width and frozen-column styling every cell gets. */
    private static _ApplyColumnLayoutToCell = (NewCell: kEditableTableCell, CurrCol: kEditableColumn): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            if (CurrCol.Width === 0) { NewCell.Cell.style.display = "none"; }
            if (CurrCol._FrozenLeft >= 0) {
                NewCell.Cell.style.position = "sticky";
                NewCell.Cell.style.left = CurrCol._FrozenLeft + "px";
                NewCell.Cell.style.zIndex = "1";
                NewCell.Cell.style.background = "inherit";
            }
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTable._ApplyColumnLayoutToCell");
        }
    };
    /** Move the add-row hint marker to the current trailing (blank) row's first cell, so a
     *  consumer's CSS can paint a faint "+ new…" placeholder there. No-op unless AddRowHint
     *  is set. Re-run whenever the trailing row changes (row grow / rebuild). */
    private _ApplyAddRowHint = (): void => kEditableTableRowOps._ApplyAddRowHint(this);
    public InsertRowAbove = (): void => kEditableTableRowOps.InsertRowAbove(this);

    /** One blank cell of an INSERTED row (InsertRowAbove). Deliberately kept as it
     *  always was: it ignores BlankNumberCellsEmpty and does not invoke a column's
     *  CustomRenderer for the empty value, unlike _CreateEmptyRowCell. Aligning the
     *  two is a behaviour change, not a formatting one, so it is left alone here. */
    private _CreateInsertedRowCell = (CurrCol: kEditableColumn, RowIndex: number, ColIndex: number): kEditableTableCell => {
        if (ks.KErrorMode === true) { return new kEditableTableCell(); }
        try {
            const NewCell: kEditableTableCell = new kEditableTableCell();
            NewCell.RowIndex = RowIndex;
            NewCell.ColumnIndex = ColIndex;
            NewCell.ColDef = CurrCol;
            NewCell.ParentTable = this;
            switch (CurrCol.Type) {
                case kHTMLEditableColType.Number:
                    NewCell.Cell.style.textAlign = "right";
                    NewCell.Cell.textContent = "0";
                    break;
                case kHTMLEditableColType.Currency:
                    NewCell.Cell.style.textAlign = "right";
                    NewCell.Cell.textContent = ks.LocalCurrencyNumber(0);
                    break;
                case kHTMLEditableColType.Checkbox:
                    NewCell.Cell.style.textAlign = "center";
                    NewCell.Cell.innerHTML = NewCell._CheckboxHtml(false);
                    break;
                case kHTMLEditableColType.Radio:
                    NewCell.Cell.style.textAlign = "center";
                    NewCell.Cell.innerHTML = NewCell._RadioHtml(false);
                    break;
            }
            if (CurrCol.DefaultValue) {
                NewCell.SetCellOrgValue(CurrCol.DefaultValue);
            } else {
                NewCell.OrgValue = "";
            }
            kEditableTable._ApplyColumnLayoutToCell(NewCell, CurrCol);
            return NewCell;
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTable._CreateInsertedRowCell");
            return new kEditableTableCell();
        }
    };
    private _LastRowHasContent = (): boolean => kEditableTableRowOps._LastRowHasContent(this);
    public GetCellByIndexes = (RowIndex:number, ColIndex:number, SuppressGrowOnEmptyLastRow:boolean = false): kEditableTableCell => kEditableTableRowOps.GetCellByIndexes(this, RowIndex, ColIndex, SuppressGrowOnEmptyLastRow);
    public PasteHandler = (Event:ClipboardEvent): void => kEditableTableRowOps.PasteHandler(this, Event);
    public RightClickPaste = (): void => kEditableTableRowOps.RightClickPaste(this);
    private _OnClipboardText = (Text: string): void => kEditableTableRowOps._OnClipboardText(this, Text);
    private _OnClipboardError = (Err: any): void => kEditableTableRowOps._OnClipboardError(this, Err);
    private _AddPasted = (): void => kEditableTableRowOps._AddPasted(this);
    private _PasteMultiCell = (Lines: (string | string[])[]): void => kEditableTableRowOps._PasteMultiCell(this, Lines);
    public SetupRowContextMenu = (): void => kEditableTableMenus.SetupRowContextMenu(this);
    public SetupCornerContextMenu = (): void => kEditableTableMenus.SetupCornerContextMenu(this);
    /** One context menu built from the caller's item list. On gating or build error the caller's
        CURRENT menu is handed back, so a failed rebuild never wipes a menu the table still holds
        — the same state the old early-return left behind. The KGlobalErrorHandler label is always
        the caller's own. */
    private _NewContextMenu = (Fallback: kContextMenu | null, Items: { Text: string; Click: () => void; Icon?: () => string; OnlyWhen?: () => boolean }[], Label: string): kContextMenu | null => kEditableTableMenus._NewContextMenu(this, Fallback, Items, Label);
    /** The row actions the row menu and the corner menu share, in the order both list them.
        The KGlobalErrorHandler label is the caller's own. */
    private _StandardContextMenuActions = (Label: string): { Text: string; Click: () => void; Icon?: () => string; OnlyWhen?: () => boolean }[] => kEditableTableMenus._StandardContextMenuActions(this, Label);
    /** The Export-to-Excel item both context menus carry (headline on the corner menu, tail on the
        row menu). The no-op default is unreachable in practice — every expression is a literal or a
        property read — and only satisfies the house pattern's return-a-default rule. */
    private _ExportMenuItem = (Label: string): { Text: string; Click: () => void; Icon?: () => string; OnlyWhen?: () => boolean } => kEditableTableMenus._ExportMenuItem(this, Label);
    /** One context menu's item list, built in the caller's order: an item is added when its OnlyWhen (if any) passes.
        DOCUMENTED EXCEPTION: no KErrorMode gate of its own - every caller (SetupRowContextMenu,
        SetupCornerContextMenu via _NewContextMenu) gates before building the menu; the
        KGlobalErrorHandler label is always the caller's own. */
    private _BuildMenuItems = (Menu: kContextMenu, Items: { Text: string; Click: () => void; Icon?: () => string; OnlyWhen?: () => boolean }[], Label: string): void => kEditableTableMenus._BuildMenuItems(this, Menu, Items, Label);
    public ExportToExcel = (): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            const Exporter: kExcelExport = new kExcelExport();
            Exporter.ExportTable(this, "Export");
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTable.ExportToExcel");
        }
    }
    public SetupColContextMenu = (): void => kEditableTableMenus.SetupColContextMenu(this);
    public RightClickSortAscending = (): void => kEditableTableMenus.RightClickSortAscending(this);
    public RightClickSortDecending = (): void => kEditableTableMenus.RightClickSortDecending(this);
    /** The two right-click sort entries in one shell: sort the right-clicked column when there is one. */
    private _RightClickSort = (Sort: (ColumnIndex?: number) => void, Label: string): void => kEditableTableMenus._RightClickSort(this, Sort, Label);
    public RightClickFilterColumn = (): void => kEditableTableMenus.RightClickFilterColumn(this);
    public RightClickClearColumnFilter = (): void => kEditableTableMenus.RightClickClearColumnFilter(this);
    public OpenAdvancedFilter = (): void => kEditableTableMenus.OpenAdvancedFilter(this);

    // Tier-2 cell-menu action (feature #19): filter the clicked cell's column to
    // its own value. List/boolean columns map to the matching operator so the
    // condition reads naturally in the chip bar.
    public RightClickFilterByValue = (): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            if (this.RightClickCell === null) { return; }
            const Column: kEditableColumn = this.RightClickCell.ColDef;
            const Prop: string = Column.KObjectPropertyName;
            const RawValue: any = this.RightClickCell.CurrentValue;
            const Text: string = ks.Nz(RawValue).toString();
            if (Column.Type === kHTMLEditableColType.Checkbox || Column.Type === kHTMLEditableColType.Radio) {
                let BoolOp: kFilterOperator = kFilterOperator.IsFalse;
                if (RawValue === true) { BoolOp = kFilterOperator.IsTrue; }
                this.SetColumnFilter(Prop, BoolOp, "");
                return;
            }
            this.SetColumnFilter(Prop, kFilterOperator.InList, "", "", [Text]);
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTable.RightClickFilterByValue");
        }
    }
    public SetupRangeContextMenu = (): void => kEditableTableMenus.SetupRangeContextMenu(this);
    public _UpdateFillMenuVisibility = (): void => kEditableTableMenus._UpdateFillMenuVisibility(this);
    /** The cell at (Row, Col) when its column is not locked, else null - one fill target. The
        KGlobalErrorHandler label is the caller's own. */
    private _FillableCell = (Row: number, Col: number, Label: string): kEditableTableCell | null => kEditableTableMenus._FillableCell(this, Row, Col, Label);
    /** One fill pass over the selected span. Down=true copies the anchor ROW's value into every
        other selected cell of each column; Down=false copies the anchor COLUMN's value into every
        other selected cell of each row. Both walk rows outer, columns inner, so SetNewValue fires
        in the same order the two old loops fired; read-only rows and locked columns are skipped
        exactly as before. The KGlobalErrorHandler label is the caller's own. */
    private _Fill = (Down: boolean, Label: string): void => kEditableTableMenus._Fill(this, Down, Label);
    public FillDown = (): void => kEditableTableMenus.FillDown(this);
    public FillRight = (): void => kEditableTableMenus.FillRight(this);
    public CopyClick = (): void => kEditableTableMenus.CopyClick(this);
    public RemoveRow = (): void => kEditableTableMenus.RemoveRow(this);
    /** Drops every row holding a selected cell, notifying RowDeletedCB for each. */
    private _RemoveSelectedRows = (): void => kEditableTableMenus._RemoveSelectedRows(this);
    /** Renumbers rows and their cells after a removal. */
    private _ReindexRows = (): void => kEditableTableMenus._ReindexRows(this);

    private _RowsToBeDeleted :kEditableTableRows|null = null;
    private _RowCounter:number = 0;
    private _RowBeingDeleted:kEditableTableRow|null = null;
    public RemoveRowContextMenuClick = (): void => kEditableTableMenus.RemoveRowContextMenuClick(this);
    public RightClickEditInForm = (): void => kEditableTableMenus.RightClickEditInForm(this);
    private _RemoveNextRow = (): void => kEditableTableMenus._RemoveNextRow(this);
    private _AfterRemoveConfirmation = (): void => kEditableTableMenus._AfterRemoveConfirmation(this);
}

// Auto-register the filter UI factory so per-column filtering is ON BY DEFAULT
// (ShowColumnFilters defaults true) with zero project code. This lives HERE — at
// the bottom of the grid module, AFTER kEditableTable is defined — on purpose:
// kEditableTableFilterUI (and the popup/dialog/model it pulls in) reference the
// grid only inside method bodies, so importing it creates no load-time cycle, and
// assigning the factory after the class exists guarantees kEditableTable is ready.
// A grid opts out per-instance via `ShowColumnFilters = false`.
import { kEditableTableFilterUI } from "../ReUsableTS/kEditableTableFilterUI";

// Assign a WRAPPER, never `kEditableTableFilterUI.Create` directly.
//
// The comment above is right that the filter UI only touches the grid inside method bodies,
// but wrong that this creates no load-time cycle: `import` is HOISTED, so writing it at the
// bottom of the file does not defer it, and kEditableTableFilterUI imports this module back.
// During that cycle the binding is still uninitialised here, so reading `.Create` at
// module-eval time threw "Cannot read properties of undefined (reading 'Create')" - which
// aborted the whole SPFx entry point, not just the filters: KTM 11.0.4.6 rendered
// "Something went wrong" with only Phrases/kLNG/ks registered. Runtime-confirmed 2026-08-03.
//
// Reading the binding inside the factory defers it to first USE, by which point every module
// in the cycle has finished evaluating and the ES live binding is populated. Behaviour is
// otherwise identical - the grid still calls FilterUIFactory(this) exactly as before.
const CreateFilterUI = (Grid: kEditableTable): kFilterUIController => {
    return kEditableTableFilterUI.Create(Grid);
};
kEditableTable.FilterUIFactory = CreateFilterUI;